/**
 * A conversa com a API — e a fronteira por onde o cálculo local vai entrar.
 *
 * Toda chamada do app passa por aqui, por dois motivos.
 *
 * O primeiro é o erro. A API recusa com 422 e um corpo que diz exatamente o
 * que falta medir (`{erro, mensagem, faltando}`). Isso é conteúdo de produto,
 * não falha técnica: a recusa que lista as análises pendentes é metade do
 * valor do app. Se cada tela tratasse `fetch` por conta própria, metade delas
 * mostraria "erro ao calcular" e jogaria essa informação fora.
 *
 * O segundo é o modo avião. A promessa é que a conta funcione sem sinal — o
 * produtor está no galpão, não no escritório. Hoje `simular()` chama a rede;
 * o motor local entra atrás desta mesma função, e nenhuma tela precisa saber
 * da troca. O que já funciona offline é a navegação pelo catálogo, que fica em
 * cache desde a primeira visita.
 *
 * Caminho relativo de propósito: não existe `VITE_API_URL`. Em
 * desenvolvimento o Vite faz proxy, em produção o nginx serve as duas coisas
 * no mesmo domínio, e não há variável de ambiente para configurar errado.
 */
import { guardar, ler } from './armazenamento.js';

/** A API recusou porque falta dado medido. Isto é resposta, não falha. */
export class ErroDeDado extends Error {
  constructor({ mensagem, faltando = {}, codigo = 'falta_dado', campo, limite }) {
    super(mensagem);
    this.name = 'ErroDeDado';
    this.codigo = codigo;
    this.faltando = faltando;
    this.campo = campo;
    this.limite = limite;
  }
}

/** Sem rede, servidor fora, ou resposta que não dá para entender. */
export class ErroDeRede extends Error {
  constructor(mensagem, { offline = false, status = null } = {}) {
    super(mensagem);
    this.name = 'ErroDeRede';
    this.offline = offline;
    this.status = status;
  }
}

function estaOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

async function chamar(caminho, opcoes = {}) {
  let resposta;
  try {
    resposta = await fetch(caminho, {
      headers: { 'Content-Type': 'application/json' },
      ...opcoes,
    });
  } catch {
    throw new ErroDeRede(
      estaOffline()
        ? 'Você está sem conexão.'
        : 'Não consegui falar com o servidor.',
      { offline: estaOffline() },
    );
  }

  if (resposta.ok) {
    try {
      return await resposta.json();
    } catch {
      throw new ErroDeRede('O servidor respondeu algo que não consegui ler.', {
        status: resposta.status,
      });
    }
  }

  let corpo = null;
  try {
    corpo = await resposta.json();
  } catch {
    /* resposta de erro sem JSON — tratada logo abaixo */
  }

  if (resposta.status === 422 && corpo && typeof corpo === 'object') {
    // Dois formatos de 422 vêm da API: falta_dado e fora_de_faixa. O terceiro
    // é o do próprio FastAPI quando a validação do corpo falha, e esse vem
    // como `detail` — texto de desenvolvedor, não de produtor.
    if (corpo.erro === 'falta_dado' || corpo.erro === 'fora_de_faixa') {
      throw new ErroDeDado({ ...corpo, codigo: corpo.erro });
    }
    const detalhe = corpo.detail;
    const texto =
      typeof detalhe === 'string'
        ? detalhe
        : Array.isArray(detalhe) && detalhe[0]?.msg
          ? String(detalhe[0].msg).replace(/^Value error,\s*/, '')
          : 'Algum dado não foi aceito.';
    throw new ErroDeDado({ mensagem: texto, codigo: 'entrada_invalida' });
  }

  if (resposta.status === 404 && corpo?.detail) {
    throw new ErroDeDado({ mensagem: String(corpo.detail), codigo: 'nao_encontrado' });
  }

  throw new ErroDeRede(
    `O servidor respondeu ${resposta.status}.`,
    { status: resposta.status },
  );
}

// --------------------------------------------------------------------------
// Catálogo — fica em cache porque é pequeno, muda pouco e é o que permite
// navegar sem sinal.
// --------------------------------------------------------------------------

const CHAVE_CATALOGO = 'catalogo';
const CHAVE_REBANHO = 'rebanho';

/**
 * Busca e guarda. Se a rede falhar e houver cópia, devolve a cópia dizendo
 * que é cópia — a tela precisa poder avisar que o dado pode estar velho em
 * vez de fingir que acabou de chegar.
 */
async function comCache(chave, caminho) {
  try {
    const dados = await chamar(caminho);
    guardar(chave, { dados, em: new Date().toISOString() });
    return { ...dados, deCache: false };
  } catch (erro) {
    const guardado = ler(chave);
    if (guardado?.dados) {
      return { ...guardado.dados, deCache: true, cacheDe: guardado.em };
    }
    throw erro;
  }
}

/**
 * As categorias de dejeto, com o que se sabe e o que falta de cada uma.
 *
 * Devolve `{ itens, total, com_analise, sem_analise, deCache }`.
 */
export function carregarCatalogo() {
  return comCache(CHAVE_CATALOGO, '/v1/substratos');
}

/**
 * Quanto de dejeto cada animal gera por dia.
 *
 * É a outra metade do que a tela de simulação precisa: o catálogo diz o que o
 * material *é*, esta rota diz *quanto* dele sai. Os rótulos daqui são os do
 * animal ("Vaca leiteira em confinamento"), e são esses que vão na pergunta
 * feita ao produtor — ele cria vaca, não "esterco de vaca leiteira em
 * lactação".
 */
export function carregarRebanho() {
  return comCache(CHAVE_REBANHO, '/v1/rebanho');
}

/** Uma categoria, com as séries de estocagem completas. */
export function carregarSubstrato(chave) {
  return chamar(`/v1/substratos/${encodeURIComponent(chave)}`);
}

/**
 * A base de evidência da estocagem, com o eixo declarado em cada leitura.
 *
 * Devolve 21 linhas para 11 medições: a mesma medição serve mais de uma
 * categoria do catálogo quando o estudo não separou. Quem consome isto conta
 * com `medicoesDistintas` de `nucleo/estocagem.js`, nunca `series.length` —
 * a contagem crua quase dobra a base de evidência.
 */
export function carregarEstocagem() {
  return chamar('/v1/substratos/estocagem');
}

// --------------------------------------------------------------------------
// Simulação
// --------------------------------------------------------------------------

/**
 * A conta. Recebe o que o usuário sabe, devolve o envelope completo.
 *
 * Campos com `null` ou `undefined` são removidos antes de enviar: a API trata
 * ausência como "não medi" e `null` explícito como a mesma coisa, mas um
 * `cabecas: null` junto de `massa_t_dia` dispararia a validação de "escala
 * informada de dois jeitos". Limpar aqui é mais seguro que lembrar em cada
 * tela.
 *
 * É aqui que o motor local entra quando existir: mesma assinatura, mesmo
 * envelope de volta, decisão tomada dentro desta função.
 */
function semVazios(entrada) {
  const corpo = {};
  for (const [k, v] of Object.entries(entrada ?? {})) {
    if (v !== null && v !== undefined && v !== '') corpo[k] = v;
  }
  return corpo;
}

export function simular(entrada) {
  return chamar('/v1/simular', {
    method: 'POST',
    body: JSON.stringify(semVazios(entrada)),
  });
}

// --------------------------------------------------------------------------
// Laboratório
//
// Quatro rotas POST, nenhuma com cache: todas recebem dados de UM laudo e
// nenhuma se repete. Guardar resposta aqui só serviria para mostrar ao
// consultor o resultado do material anterior.
// --------------------------------------------------------------------------

/**
 * "Este ensaio vale?" — antes de o resultado sair do laboratório.
 *
 * Manda só o que foi informado: a resposta separa o que foi conferido do que
 * não deu para conferir, e essa separação é metade do valor da rota.
 */
export function conferirEnsaio(entrada) {
  return chamar('/v1/laboratorio/ensaio/conferir', {
    method: 'POST',
    body: JSON.stringify(semVazios(entrada)),
  });
}

/** "O que esta caracterização está dizendo?" — DQO, COT e, se houver, DBO. */
export function lerLaudo(entrada) {
  return chamar('/v1/laboratorio/laudo/oxidacao', {
    method: 'POST',
    body: JSON.stringify(semVazios(entrada)),
  });
}

/**
 * "Quanto este material deve render?" — o comitê inteiro, sem média.
 *
 * `elementar` é objeto aninhado e só vai quando tem os cinco elementos: meio
 * CHNOS faz a API recusar com 422, e recusar por campo faltando é pior que
 * não mandar o bloco.
 */
export function preverBmp(entrada) {
  const corpo = semVazios(entrada);
  const el = semVazios(entrada.elementar ?? {});
  delete corpo.elementar;
  if (['c', 'h', 'o'].every((k) => el[k] !== undefined)) corpo.elementar = el;
  return chamar('/v1/laboratorio/bmp/prever', {
    method: 'POST',
    body: JSON.stringify(corpo),
  });
}

/**
 * O teto estequiométrico de Boyle, sozinho.
 *
 * Não tem tela: `preverBmp` já devolve este mesmo número em `teto_boyle`, a
 * partir da mesma análise elementar, e uma tela cujo conteúdo inteiro é um
 * número que outra já mostra acaba divergindo dela. A função existe porque a
 * rota existe, e a igualdade entre as duas é medida em
 * `api/tests/test_laboratorio.py` — no dia em que divergirem, o teste cai e a
 * decisão de não ter a tela precisa ser revista.
 */
export function tetoDeBoyle(elementar) {
  return chamar('/v1/laboratorio/bmp/teto', {
    method: 'POST',
    body: JSON.stringify({ elementar: semVazios(elementar) }),
  });
}

/** O servidor está de pé e calculando? `/saude` roda uma conta de verdade. */
export function saude() {
  return chamar('/saude');
}
